import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { ArrowRight, CheckCircle2, ChevronDown, Sparkles } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import AdvisorCard from "@/components/AdvisorCard";
import MiniLeadForm from "@/components/MiniLeadForm";
import PdfDownloadCard from "@/components/PdfDownloadCard";
import { PRODUCT_ICON_MAP } from "@/components/ProductCard";
import usePageSEO from "@/hooks/usePageSEO";

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div data-testid={`persona-faq-${idx}`} className="border-b border-lw-navy/10 py-4">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex items-start justify-between w-full text-left gap-4">
        <span className="font-sans-ui font-semibold text-lw-navy">{q}</span>
        <ChevronDown size={18} className={`flex-shrink-0 mt-1 text-lw-navy/60 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="mt-3 text-sm text-lw-muted leading-relaxed">{a}</p>}
    </div>
  );
}

export default function Persona() {
  const { slug } = useParams();
  const [persona, setPersona] = useState(null);
  const [products, setProducts] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, prods, advs] = await Promise.all([
          apiGet(`/personas/${slug}`),
          apiGet("/products"),
          apiGet("/advisors"),
        ]);
        if (cancelled) return;
        setPersona(p);
        setProducts(prods || []);
        setAdvisors(advs || []);
      } catch {
        if (!cancelled) setError("This audience page could not be found.");
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  usePageSEO(
    persona
      ? {
          title: `${persona.name.replace("For ", "")} Financial Strategy`,
          description: persona.hero_subhead,
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "WebPage",
              name: persona.name,
              description: persona.hero_subhead,
            },
            persona.faqs?.length
              ? {
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: persona.faqs.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                  })),
                }
              : null,
          ].filter(Boolean),
        }
      : { title: "For You", description: "Lifespan Wealth Partners persona strategies." }
  );

  if (error) {
    return (
      <div className="min-h-screen bg-lw-ivory">
        <Nav />
        <main className="max-w-3xl mx-auto px-5 pt-32 pb-20">
          <h1 className="font-serif-display text-3xl text-lw-navy">Not found.</h1>
          <p className="mt-3 text-lw-muted">{error}</p>
          <Link to="/" className="btn-gold mt-6 inline-flex">Back home</Link>
        </main>
        <Footer />
      </div>
    );
  }
  if (!persona) {
    return (
      <div className="min-h-screen bg-lw-ivory">
        <Nav />
        <main className="max-w-3xl mx-auto px-5 pt-32 pb-20">
          <p className="text-lw-muted">Loading…</p>
        </main>
        <Footer />
      </div>
    );
  }

  const recommendedProducts = persona.recommended_product_ids
    .map((id) => products.find((p) => p.id === id))
    .filter(Boolean);
  const advisor = advisors.find((a) => a.id === persona.matched_advisor_id);
  const applyHref = `/apply?source=persona-${persona.slug}`;
  const concernOptions = persona.concerns.map((c) => ({ value: c.title, label: c.title }));

  return (
    <div data-testid={`persona-page-${persona.slug}`} className="min-h-screen bg-lw-ivory">
      <Nav />
      <main className="pt-28 sm:pt-32 lg:pt-36 pb-20">

        {/* Hero */}
        <section className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <span className="lw-eyebrow">{persona.name}</span>
              <h1 data-testid="persona-headline" className="mt-3 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-lw-navy leading-[1.05]">
                {persona.hero_headline}
              </h1>
              <p className="mt-5 text-base sm:text-lg text-lw-muted leading-relaxed max-w-2xl">{persona.hero_subhead}</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to={applyHref} data-testid="persona-cta-primary" className="btn-gold">
                  Get my personalized strategy <ArrowRight size={18} />
                </Link>
                <a href="#products" data-testid="persona-cta-secondary" className="btn-ghost-navy">See your recommended products</a>
              </div>
            </div>
            <div className="lg:col-span-2 relative">
              <div className="aspect-[4/5] rounded-3xl relative overflow-hidden bg-lw-navy">
                <div className="absolute inset-0 opacity-95"
                  style={{ background: "radial-gradient(circle at 30% 20%, rgba(212,162,76,0.45) 0%, rgba(11,37,69,0) 60%), linear-gradient(135deg, #0B2545 0%, #1E3A8A 100%)" }}
                />
                <div className="absolute inset-0 flex items-center justify-center p-10 text-center text-lw-ivory">
                  <Sparkles size={56} className="text-lw-gold" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* We see you - concerns */}
        <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-24">
          <div className="reveal max-w-2xl">
            <span className="lw-eyebrow">We see you</span>
            <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight">Top concerns we hear from people like you.</h2>
          </div>
          <div data-testid="persona-concerns" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mt-10">
            {persona.concerns.map((c, i) => (
              <div key={i} data-testid={`persona-concern-${i}`} className="rounded-2xl bg-white border border-lw-navy/10 p-5">
                <div className="w-10 h-10 rounded-xl bg-lw-gold/15 text-lw-navy flex items-center justify-center mb-3">
                  <span aria-hidden="true" className="text-lw-gold font-serif-display">{i + 1}</span>
                </div>
                <h3 className="font-serif-display text-base text-lw-navy">{c.title}</h3>
                <p className="mt-2 text-sm text-lw-muted leading-relaxed">{c.line}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Recommended product stack */}
        <section id="products" className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-24 scroll-mt-24">
          <div className="reveal max-w-2xl">
            <span className="lw-eyebrow">Your recommended stack</span>
            <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight">The products that fit your situation.</h2>
          </div>
          <div data-testid="persona-products" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {recommendedProducts.map((p) => {
              const Icon = PRODUCT_ICON_MAP[p.id] || Sparkles;
              return (
                <Link
                  key={p.id}
                  to={`/products/${p.id}`}
                  data-testid={`persona-product-${p.id}`}
                  className="lw-card p-6 group flex flex-col h-full"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, rgba(11,37,69,0.06), rgba(212,162,76,0.14))", color: "#0B2545" }}>
                      <Icon size={20} />
                    </div>
                    <span className="lw-pill text-[10px]">{p.wealth_pyramid_layer}</span>
                  </div>
                  <h3 className="font-serif-display text-lg text-lw-navy leading-tight">{p.name}</h3>
                  <p className="mt-2 text-sm text-lw-muted leading-relaxed">{persona.product_why[p.id] || p.tagline}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-lw-navy group-hover:text-[#8a6a2b]">Learn more <ArrowRight size={14} /></span>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Strategy narrative */}
        <section className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 mt-24">
          <div className="reveal max-w-2xl">
            <span className="lw-eyebrow">How wealth compounds for you</span>
            <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight">A three-step strategy for {persona.name.replace("For ", "").toLowerCase()}.</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
            {[
              { key: "protect", label: "Step 1 — Protect" },
              { key: "accumulate", label: "Step 2 — Accumulate" },
              { key: "transfer", label: "Step 3 — Transfer & Distribute" },
            ].map((s) => (
              <div key={s.key} data-testid={`persona-strategy-${s.key}`} className="rounded-2xl bg-white border border-lw-navy/10 p-6">
                <span className="text-xs uppercase tracking-[0.18em] text-lw-gold font-semibold">{s.label}</span>
                <p className="mt-3 text-sm text-lw-navy/85 leading-relaxed">{persona.strategy_narrative[s.key]}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Real scenario card */}
        <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 mt-24">
          <div className="rounded-3xl bg-lw-navy text-lw-ivory p-8 sm:p-12 relative overflow-hidden">
            <div aria-hidden className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full" style={{ background: "radial-gradient(circle, rgba(212,162,76,0.2) 0%, rgba(11,37,69,0) 70%)" }} />
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <span className="text-xs uppercase tracking-[0.2em] text-lw-gold font-semibold">Real scenario, real numbers</span>
                <h3 className="mt-3 font-serif-display text-2xl sm:text-3xl leading-tight">A hypothetical client in your situation.</h3>
                <p className="mt-3 text-sm text-lw-ivory/75 leading-relaxed">See how a layered strategy plays out across protection, accumulation, and transfer — for someone like you.</p>
                <Link to={`/insights/scenarios/${persona.scenario_slug}`} data-testid="persona-scenario-cta" className="btn-gold mt-6 inline-flex">
                  See full case study <ArrowRight size={18} />
                </Link>
              </div>
              <div className="flex justify-center lg:justify-end">
                <div className="w-32 h-32 rounded-full bg-lw-gold/20 border border-lw-gold/40 flex items-center justify-center font-serif-display text-3xl text-lw-gold">
                  {persona.name.charAt(4)}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Lifespan trust pillars */}
        <section className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 mt-24">
          <div className="reveal max-w-2xl">
            <span className="lw-eyebrow">Why Lifespan</span>
            <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight">What makes us a fit for you.</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {persona.trust_pillars.map((tp, i) => (
              <div key={i} data-testid={`persona-trust-${i}`} className="rounded-2xl bg-lw-ivory-warm border border-lw-navy/10 p-5">
                <CheckCircle2 size={20} className="text-lw-gold mb-3" />
                <h3 className="font-serif-display text-base text-lw-navy leading-tight">{tp.title}</h3>
                <p className="mt-2 text-sm text-lw-muted leading-relaxed">{tp.body}</p>
              </div>
            ))}
          </div>
          {advisor && (
            <div className="mt-10 max-w-md">
              <AdvisorCard advisor={advisor} />
            </div>
          )}
        </section>

        {/* Mini lead form */}
        <section className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 mt-24 space-y-6">
          <PdfDownloadCard
            endpoint={`/pdf/guide/${persona.slug}`}
            filename={`lifespan-${persona.slug}-playbook.pdf`}
            title={`Get the ${persona.name.replace('For ', '')} Wealth Playbook (Free PDF)`}
            subtitle="12-page guide, no fluff. Emailed to you instantly."
            testIdPrefix={`pdf-${persona.slug}`}
          />
          <MiniLeadForm
            source={`persona-${persona.slug}`}
            title="Get a strategy made for you"
            subtitle="We'll be in touch within 1 business day."
            withPhone
            concernOptions={concernOptions}
            context={{ persona: persona.slug }}
            testIdPrefix="persona-lead"
          />
        </section>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 mt-24">
          <div className="reveal max-w-2xl">
            <span className="lw-eyebrow">FAQ</span>
            <h2 className="mt-3 font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight">Common questions, plainly answered.</h2>
          </div>
          <div data-testid="persona-faqs" className="mt-8">
            {persona.faqs.map((f, i) => <FAQItem key={i} idx={i} q={f.q} a={f.a} />)}
          </div>
        </section>

        {/* Related Services band */}
        <section
          data-testid="persona-related-services"
          className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-24"
        >
          <span className="lw-eyebrow">Related services</span>
          <h2 className="mt-3 font-serif-display text-2xl sm:text-3xl text-lw-navy leading-tight">
            Explore the service categories most relevant to your stage.
          </h2>
          <div className="mt-6 flex flex-wrap gap-3">
            {(persona.related_service_slugs || ["life-insurance", "annuities", "long-term-care", "wealth-strategy"]).map((sl) => (
              <Link
                key={sl}
                to={`/services/${sl}`}
                data-testid={`persona-service-link-${sl}`}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-lw-navy/15 bg-white text-sm text-lw-navy hover:border-lw-gold transition-colors"
              >
                {sl.replace(/-/g, " ")} <ArrowRight size={14} />
              </Link>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 mt-24">
          <div className="rounded-3xl bg-lw-ivory-warm border border-lw-gold/30 p-8 sm:p-12">
            <h2 className="font-serif-display text-3xl sm:text-4xl text-lw-navy leading-tight max-w-xl">Ready to put a strategy together?</h2>
            <p className="mt-3 text-base text-lw-muted max-w-xl">A 30-minute discovery call. No pressure. No products pitched on the first call.</p>
            <Link to={applyHref} data-testid="persona-final-cta" className="btn-gold mt-6 inline-flex">Book a no-obligation discovery call <ArrowRight size={18} /></Link>
          </div>
        </section>

        {/* Compliance microcopy */}
        <section className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 mt-12">
          <p data-testid="persona-disclaimer" className="text-xs text-lw-muted leading-relaxed">{persona.compliance_microcopy}</p>
        </section>
      </main>
      <Footer />
    </div>
  );
}
